import React, { useEffect } from 'react';
import { useMessageHistory } from '../hooks/useMessageHistory';
import { useCurrentUserEmsTeam } from '../../hooks/useCurrentUserEmsTeam';
import { useUsingDashboardHospitals } from '../hooks/useUsingDashboardHospitals';
import { MessageItem } from './MessageItem';

type Props = {
  /** ダイアログの開閉状態。 */
  open: boolean;
  /** ダイアログを閉じる関数。 */
  onClose: VoidFunction;
};

/** 登録履歴参照ダイアログのコンポーネント。 */
export const HistoryDialog = ({ open, onClose: handleClose }: Props) => {
  const { messageHistory, isLoading, error, deleteMessage, reloadFromAPI } =
    useMessageHistory();
  const { emsTeam } = useCurrentUserEmsTeam();

  // ダッシュボード使用病院リストを取得
  const {
    hospitals: availableHospitals,
    isLoading: isHospitalsLoading,
    error: hospitalsError,
    fetchHospitals,
  } = useUsingDashboardHospitals();

  // ダイアログが開いたときに最新データを読み込み
  useEffect(() => {
    if (open) {
      reloadFromAPI();
      // 病院リストも取得
      if (emsTeam?.emsTeamId) {
        fetchHospitals(emsTeam.emsTeamId);
      }
    }
  }, [open, reloadFromAPI, emsTeam?.emsTeamId, fetchHospitals]);

  // 病院リストの読み込み状態を統合
  const isLoadingData = isLoading || isHospitalsLoading;

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-textPrimary bg-opacity-45 z-10" />
      <div className="bg-surface rounded-[28px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[1200px] max-h-[90vh] p-[32px] absolute z-20 flex flex-col">
        {/* ヘッダー部分（固定） */}
        <div className="flex items-center justify-between flex-shrink-0 pb-[24px]">
          <h1 className="text-[20px] text-textPrimary noto-sans-jp-bold">
            登録履歴
          </h1>
          <button
            className="w-[32px] h-[32px] flex items-center justify-center rounded-full hover:bg-gray-100 text-textSecondary hover:text-textPrimary transition-colors"
            onClick={handleClose}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* エラーメッセージ表示 */}
        {hospitalsError && (
          <div className="text-error text-[14px] noto-sans-jp-regular flex-shrink-0 pb-[24px]">
            {hospitalsError}
          </div>
        )}

        {/* メッセージリスト部分（スクロール可能） */}
        <div className="flex flex-col gap-[16px] overflow-y-auto flex-1 min-h-0">
          {isLoadingData ? (
            <div className="text-center py-[32px]">
              <p className="text-textSecondary noto-sans-jp-regular">
                読み込み中...
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-[32px]">
              <p className="text-error noto-sans-jp-regular">{error}</p>
            </div>
          ) : messageHistory.length === 0 ? (
            <div className="text-center py-[32px]">
              <p className="text-textSecondary noto-sans-jp-regular">
                登録済みのメッセージはありません
              </p>
            </div>
          ) : (
            messageHistory.map(message => (
              <MessageItem
                key={message.messageId}
                message={message}
                availableHospitals={availableHospitals}
                onDelete={deleteMessage}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
};
